import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient, downloadFile } from '@/lib/api/client'
import type { PageResponse } from '@/lib/api/types'
import type {
  AirTicketClaimView, AirTicketEntitlementView, BasicPayBasisView, EmployeePayElementView,
  EmployeeProvisionsView,
  ExplainView, GpssaConfigView, GpssaContributionView, JournalBatchView, MyPayslipView,
  PayElementVersionView,
  PayElementView, PayrollLineView, PayrollRunEventView, PayrollRunView, PayslipView,
  ProvisionLedgerView, ProvisionType,
} from '../types'
import type { CalculationResultView, ProvisionResultView } from '../types'

const BASE = '/v1/payroll'

export function usePayrollRuns() {
  return useQuery({
    queryKey: ['payroll', 'runs'],
    queryFn: async () => (await apiClient.get<PayrollRunView[]>(`${BASE}/runs`)).data,
  })
}

export function usePayrollRun(id: string | undefined) {
  return useQuery({
    queryKey: ['payroll', 'runs', id],
    enabled: Boolean(id),
    queryFn: async () => (await apiClient.get<PayrollRunView>(`${BASE}/runs/${id}`)).data,
  })
}

export function usePayrollRunEvents(id: string | undefined) {
  return useQuery({
    queryKey: ['payroll', 'runs', id, 'events'],
    enabled: Boolean(id),
    queryFn: async () =>
      (await apiClient.get<PayrollRunEventView[]>(`${BASE}/runs/${id}/events`)).data,
  })
}

/** The register. Opening it is recorded in the sensitive access log. */
export function usePayrollLines(id: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ['payroll', 'runs', id, 'lines'],
    enabled: Boolean(id) && enabled,
    queryFn: async () =>
      (await apiClient.get<PayrollLineView[]>(`${BASE}/runs/${id}/lines`)).data,
  })
}

export function usePayslip(runId: string | undefined, employeeId: string | undefined) {
  return useQuery({
    queryKey: ['payroll', 'runs', runId, 'payslip', employeeId],
    enabled: Boolean(runId) && Boolean(employeeId),
    queryFn: async () =>
      (await apiClient.get<PayslipView>(`${BASE}/runs/${runId}/payslip/${employeeId}`)).data,
  })
}

export function useExplain(lineId: string | undefined) {
  return useQuery({
    queryKey: ['payroll', 'explain', lineId],
    enabled: Boolean(lineId),
    queryFn: async () =>
      (await apiClient.get<ExplainView>(`${BASE}/lines/${lineId}/explain`)).data,
  })
}

export function useJournals(id: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ['payroll', 'runs', id, 'journals'],
    enabled: Boolean(id) && enabled,
    queryFn: async () =>
      (await apiClient.get<JournalBatchView[]>(`${BASE}/runs/${id}/journals`)).data,
  })
}

export function usePayrollRunMutations() {
  const queryClient = useQueryClient()
  // A transition changes totals, the journal and the event history at once.
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['payroll'] })

  // Written out rather than generated by a helper: a hook called inside another function is
  // a Rules-of-Hooks violation even when the call order happens to be stable.
  const lock = useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason?: string }) =>
      (await apiClient.post<PayrollRunView>(`${BASE}/runs/${id}/lock`, { reason })).data,
    onSuccess: invalidate,
  })
  const approve = useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason?: string }) =>
      (await apiClient.post<PayrollRunView>(`${BASE}/runs/${id}/approve`, { reason })).data,
    onSuccess: invalidate,
  })
  const markPaid = useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason?: string }) =>
      (await apiClient.post<PayrollRunView>(`${BASE}/runs/${id}/mark-paid`, { reason })).data,
    onSuccess: invalidate,
  })
  const close = useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason?: string }) =>
      (await apiClient.post<PayrollRunView>(`${BASE}/runs/${id}/close`, { reason })).data,
    onSuccess: invalidate,
  })

  return {
    open: useMutation({
      mutationFn: async (body: {
        legalEntityId: string
        establishmentId?: string | null
        year: number
        month: number
      }) => (await apiClient.post<PayrollRunView>(`${BASE}/runs`, body)).data,
      onSuccess: invalidate,
    }),
    calculate: useMutation({
      mutationFn: async (id: string) =>
        (await apiClient.post<CalculationResultView>(`${BASE}/runs/${id}/calculate`)).data,
      onSuccess: invalidate,
    }),
    lock,
    approve,
    markPaid,
    close,
    reopen: useMutation({
      mutationFn: async ({ id, reason }: { id: string; reason: string }) =>
        (await apiClient.post<PayrollRunView>(`${BASE}/runs/${id}/reopen`, { reason })).data,
      onSuccess: invalidate,
    }),
  }
}

// --- elements --------------------------------------------------------------

export function usePayElements() {
  return useQuery({
    queryKey: ['payroll', 'elements'],
    queryFn: async () => (await apiClient.get<PayElementView[]>(`${BASE}/elements`)).data,
  })
}

export function usePayElementVersions(elementId: string | undefined) {
  return useQuery({
    queryKey: ['payroll', 'elements', elementId, 'versions'],
    enabled: Boolean(elementId),
    queryFn: async () =>
      (await apiClient.get<PayElementVersionView[]>(`${BASE}/elements/${elementId}/versions`)).data,
  })
}

export function useEmployeePayElements(employeeId: string | undefined) {
  return useQuery({
    queryKey: ['payroll', 'employees', employeeId, 'elements'],
    enabled: Boolean(employeeId),
    queryFn: async () =>
      (await apiClient.get<EmployeePayElementView[]>(
        `${BASE}/employees/${employeeId}/elements`)).data,
  })
}

/**
 * Sets what one person is paid from one element, from a date.
 *
 * <p>The direct path: the server closes the previous amount the day before, treats the same
 * start date as a correction, and writes every call to the sensitive access log. A rise that
 * needs approving goes through salary revisions instead.
 */
export function useAssignEmployeePayElement(employeeId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: {
      elementId: string; effectiveFrom: string; amount: number
      currency: string | null; notes: string | null
    }) => (await apiClient.put<EmployeePayElementView>(
      `${BASE}/employees/${employeeId}/elements`, body)).data,
    onSuccess: () => queryClient.invalidateQueries({
      queryKey: ['payroll', 'employees', employeeId, 'elements'],
    }),
  })
}

/**
 * Several of one person's amounts from one date, all written or none. `amount: null` leaves an
 * element alone and `0` stops it — the dialog sends only the rows somebody changed.
 */
export function useAssignEmployeePayElements(employeeId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: {
      effectiveFrom: string; notes: string | null
      items: { elementId: string; amount: number | null }[]
    }) => (await apiClient.put<EmployeePayElementView[]>(
      `${BASE}/employees/${employeeId}/elements/bulk`, body)).data,
    onSuccess: () => queryClient.invalidateQueries({
      queryKey: ['payroll', 'employees', employeeId, 'elements'],
    }),
  })
}

export function usePayElementMutations() {
  const queryClient = useQueryClient()
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['payroll', 'elements'] })

  return {
    create: useMutation({
      mutationFn: async (body: Record<string, unknown>) =>
        (await apiClient.post<PayElementView>(`${BASE}/elements`, body)).data,
      onSuccess: invalidate,
    }),
    update: useMutation({
      mutationFn: async ({ id, body }: { id: string; body: Record<string, unknown> }) =>
        (await apiClient.put<PayElementView>(`${BASE}/elements/${id}`, body)).data,
      onSuccess: invalidate,
    }),
    /**
     * Retires an element.
     *
     * <p>Never a delete: a payslip line names the element that produced it, and somebody
     * disputing a figure two years from now has to be able to look it up. Refused while it is
     * in anybody's package or part of the gratuity basis.
     */
    retire: useMutation({
      mutationFn: async (id: string) => {
        await apiClient.post(`${BASE}/elements/${id}/deactivate`)
      },
      onSuccess: invalidate,
    }),
    addVersion: useMutation({
      mutationFn: async ({ id, body }: { id: string; body: Record<string, unknown> }) =>
        (await apiClient.post<PayElementVersionView>(
          `${BASE}/elements/${id}/versions`, body)).data,
      onSuccess: invalidate,
    }),
  }
}

// --- provisions, GPSSA, air tickets ----------------------------------------

export function useBasicPayBasis() {
  return useQuery({
    queryKey: ['payroll', 'basic-pay-basis'],
    queryFn: async () =>
      (await apiClient.get<BasicPayBasisView>(`${BASE}/basic-pay-basis`)).data,
    staleTime: 60_000,
  })
}

export function useProvisions(provisionType: ProvisionType, year: number, month: number) {
  return useQuery({
    queryKey: ['payroll', 'provisions', provisionType, year, month],
    queryFn: async () =>
      (await apiClient.get<ProvisionLedgerView[]>(`${BASE}/provisions`, {
        params: { provisionType, year, month },
      })).data,
    placeholderData: (previous) => previous,
  })
}

export function useGpssaConfig() {
  return useQuery({
    queryKey: ['payroll', 'gpssa', 'config'],
    queryFn: async () => (await apiClient.get<GpssaConfigView[]>(`${BASE}/gpssa/config`)).data,
  })
}

export function useGpssaReport(year: number, month: number) {
  return useQuery({
    queryKey: ['payroll', 'gpssa', 'report', year, month],
    queryFn: async () =>
      (await apiClient.get<GpssaContributionView[]>(`${BASE}/gpssa/report`, {
        params: { year, month },
      })).data,
    placeholderData: (previous) => previous,
  })
}

export function useAirTicketEntitlements() {
  return useQuery({
    queryKey: ['payroll', 'air-tickets', 'entitlements'],
    queryFn: async () =>
      (await apiClient.get<AirTicketEntitlementView[]>(`${BASE}/air-tickets/entitlements`)).data,
  })
}

export function useAirTicketClaims(employeeId: string | undefined) {
  return useQuery({
    queryKey: ['payroll', 'air-tickets', 'claims', employeeId],
    enabled: Boolean(employeeId),
    queryFn: async () =>
      (await apiClient.get<AirTicketClaimView[]>(`${BASE}/air-tickets/claims`, {
        params: { employeeId },
      })).data,
  })
}

/** One person's gratuity, leave salary and air-ticket balances. Written to the access log. */
export function useEmployeeProvisions(employeeId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ['payroll', 'provisions', 'employee', employeeId],
    enabled: Boolean(employeeId) && enabled,
    queryFn: async () =>
      (await apiClient.get<EmployeeProvisionsView>(`${BASE}/employees/${employeeId!}/provisions`)).data,
  })
}

/**
 * One person's month-by-month movement for a single provision.
 *
 * <p>Paged, because this grows by a row a month for every year somebody is employed. The
 * server fixes the order — newest month first — so there is no sort to pass: a balance
 * history reads in one direction and any other would be a way to misread it.
 */
export function useProvisionHistory(
  employeeId: string | undefined,
  provisionType: string | undefined,
  page = 0,
  size = 24,
) {
  return useQuery({
    queryKey: ['payroll', 'provisions', 'history', employeeId, provisionType, page, size],
    enabled: Boolean(employeeId) && Boolean(provisionType),
    queryFn: async () =>
      (await apiClient.get<PageResponse<ProvisionLedgerView>>(
        `${BASE}/employees/${employeeId!}/provisions/${provisionType!}/history`,
        { params: { page, size } },
      )).data,
  })
}

export function useProvisionMutations() {
  const queryClient = useQueryClient()
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['payroll'] })

  return {
    accrue: useMutation({
      mutationFn: async (body: {
        legalEntityId: string
        year: number
        month: number
        provisionType: string
      }) => (await apiClient.post<ProvisionResultView>(`${BASE}/provisions/accrue`, body)).data,
      onSuccess: invalidate,
    }),
    saveGpssaConfig: useMutation({
      mutationFn: async (body: Record<string, unknown>) =>
        (await apiClient.put<GpssaConfigView>(`${BASE}/gpssa/config`, body)).data,
      onSuccess: invalidate,
    }),
  }
}

/**
 * My own payslips.
 *
 * <p>No employee id anywhere: the endpoints read it from the session, which is what makes
 * these the two calls an employee is allowed to make about pay.
 */
export function useMyPayslips() {
  return useQuery({
    queryKey: ['payroll', 'my', 'payslips'],
    queryFn: async () => (await apiClient.get<MyPayslipView[]>(`${BASE}/my/payslips`)).data,
  })
}

export function useMyPayslip(runId: string | undefined) {
  return useQuery({
    enabled: Boolean(runId),
    queryKey: ['payroll', 'my', 'payslip', runId],
    queryFn: async () => (await apiClient.get<PayslipView>(`${BASE}/my/payslips/${runId}`)).data,
  })
}

/** The PDF, fetched through the authenticated client rather than a plain link. */
export async function downloadMyPayslip(runId: string, filename: string) {
  await downloadFile(`${BASE}/my/payslips/${runId}/pdf`, filename)
}
